"use client"

import { useRef, useState, useEffect } from "react"
import { motion, useAnimation } from "framer-motion"

const useOnceInView = (options = {}) => {
  const ref = useRef(null)
  const [hasBeenInView, setHasBeenInView] = useState(false)

  useEffect(() => {
    if (!ref.current || hasBeenInView) return
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenInView(true)
        }
      },
      { threshold: 0.3, ...options }
    )
    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [hasBeenInView, options])

  return [ref, hasBeenInView]
}

/**
 * AnimatedVillaImage
 * @param {string} src - Image URL
 * @param {string} alt - Alt text
 * @param {string} className - Additional classes
 * @param {object} style - Inline styles
 * @param {React.ReactNode} children - Optional overlay content
 */
export default function AnimatedVillaImage({ src, alt, className = "", style = {}, children }) {
  const [ref, inView] = useOnceInView()
  const controls = useAnimation()

  useEffect(() => {
    if (inView) {
      controls.start({
        opacity: 1,
        scale: 1,
        transition: { duration: 1, ease: [0.22, 1, 0.36, 1] }
      })
    }
  }, [inView, controls])

  return (
    <div
      ref={ref}
      className={`relative w-full h-[60vw] max-h-[420px] md:h-[420px] overflow-hidden rounded-2xl shadow-lg ${className}`}
      style={style}
    >
      <motion.img
        src={src}
        alt={alt}
        initial={{ opacity: 0, scale: 1.08 }}
        animate={controls}
        className="w-full h-full object-cover"
        style={{ willChange: "opacity, transform" }}
        draggable={false}
      />
      {children && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          {children}
        </div>
      )}
    </div>
  )
}